import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import approvalsService, { ApprovalStats } from '../../services/approvals';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<ApprovalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await approvalsService.getStats();
      setStats(data);
    } catch (err) {
      setError('Failed to load statistics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Pending Approvals',
      value: stats?.total_pending || 0,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      ),
      color: 'warning',
      link: '/approvals',
    },
    {
      title: 'Approved Today',
      value: stats?.total_approved_today || 0,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 11 12 14 22 4"/>
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
        </svg>
      ),
      color: 'success',
      link: '/approved',
    },
    {
      title: 'Rejected Today',
      value: stats?.total_rejected_today || 0,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      ),
      color: 'danger',
      link: '/rejected',
    },
    {
      title: 'Average Quality',
      value: `${Math.round(stats?.avg_quality_score || 0)}%`,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ),
      color: 'info',
    },
  ];

  const qualityBreakdown = [
    { tier: 'Premium', count: stats?.pending_premium || 0, color: '#10B981' },
    { tier: 'Standard', count: stats?.pending_standard || 0, color: '#F59E0B' },
    { tier: 'Bulk', count: stats?.pending_bulk || 0, color: '#EF4444' },
  ];

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p className="page-subtitle">Overview of your image approval system</p>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading statistics...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p>{error}</p>
          <button onClick={loadStats} className="btn btn-primary">
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="stats-grid">
            {statCards.map((stat) => (
              <Link
                key={stat.title}
                to={stat.link || '#'}
                className={`stat-card stat-${stat.color}`}
              >
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-content">
                  <h3 className="stat-value">{stat.value}</h3>
                  <p className="stat-title">{stat.title}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Quality Breakdown */}
          <div className="dashboard-row">
            <div className="dashboard-card">
              <h2 className="card-title">Quality Breakdown</h2>
              <div className="quality-breakdown">
                {qualityBreakdown.map((item) => (
                  <div key={item.tier} className="quality-item">
                    <div className="quality-header">
                      <span className="quality-label">{item.tier}</span>
                      <span className="quality-count">{item.count}</span>
                    </div>
                    <div className="quality-bar">
                      <div
                        className="quality-fill"
                        style={{
                          width: `${(item.count / (stats?.total_pending || 1)) * 100}%`,
                          backgroundColor: item.color,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-card">
              <h2 className="card-title">Quick Actions</h2>
              <div className="quick-actions">
                <Link to="/approvals" className="action-btn action-primary">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                  </svg>
                  Review Pending Images
                </Link>
                <Link to="/rejected" className="action-btn action-secondary">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"/>
                  </svg>
                  Analyze Rejected Images
                </Link>
                <button onClick={loadStats} className="action-btn action-ghost">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/>
                  </svg>
                  Refresh Stats
                </button>
              </div>
            </div>
          </div>

          {/* Recent Activity Placeholder */}
          <div className="dashboard-card">
            <h2 className="card-title">Recent Activity</h2>
            <div className="activity-placeholder">
              <p className="text-muted">Activity feed coming soon...</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;